import { css } from "@emotion/react";
import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { graphql, useMutation } from "react-relay";

import {
  Icon,
  Icons,
  Keyboard,
  VisuallyHidden,
} from "@phoenix/components";
import { usePlaygroundContext } from "@phoenix/contexts/PlaygroundContext";
import { useModifierKey } from "@phoenix/hooks/useModifierKey";

import type { PlaygroundRunButtonDismissMutation } from "./__generated__/PlaygroundRunButtonDismissMutation.graphql";
import type { PlaygroundRunButtonStopMutation } from "./__generated__/PlaygroundRunButtonStopMutation.graphql";

const runBtnCSS = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: var(--ac-global-color-primary, #00685f);
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 104, 95, 0.2);
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    background-color: var(--ac-global-color-primary-container, #005049);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 104, 95, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const stopBtnCSS = css`
  ${runBtnCSS};
  background-color: var(--ac-global-color-danger, #d32f2f);
  box-shadow: 0 1px 3px rgba(211, 47, 47, 0.2);

  &:hover {
    background-color: #b71c1c;
    box-shadow: 0 3px 8px rgba(211, 47, 47, 0.3);
  }
`;

const kbdWrapCSS = css`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  font-size: 11px;
  opacity: 0.9;
`;

export function PlaygroundRunButton() {
  const modifierKey = useModifierKey();
  const { runPlaygroundInstances, cancelPlaygroundInstances, instances } =
    usePlaygroundContext((state) => ({
      runPlaygroundInstances: state.runPlaygroundInstances,
      cancelPlaygroundInstances: state.cancelPlaygroundInstances,
      instances: state.instances,
    }));
  const isRunning = usePlaygroundContext((state) =>
    state.instances.some((instance) => instance.activeRunId != null)
  );

  const [stopExperiment] = useMutation<PlaygroundRunButtonStopMutation>(graphql`
    mutation PlaygroundRunButtonStopMutation($experimentId: ID!) {
      stopExperiment(experimentId: $experimentId) {
        job {
          id
          status
        }
      }
    }
  `);

  const [dismissExperiment] =
    useMutation<PlaygroundRunButtonDismissMutation>(graphql`
      mutation PlaygroundRunButtonDismissMutation($experimentId: ID!) {
        dismissExperiment(experimentId: $experimentId) {
          experiment {
            id
          }
        }
      }
    `);

  const toggleRunning = useCallback(() => {
    if (isRunning) {
      for (const instance of instances) {
        if (instance.experiment) {
          stopExperiment({
            variables: { experimentId: instance.experiment.id },
          });
          dismissExperiment({
            variables: { experimentId: instance.experiment.id },
          });
        }
      }
      cancelPlaygroundInstances();
    } else {
      runPlaygroundInstances();
    }
  }, [
    isRunning,
    cancelPlaygroundInstances,
    runPlaygroundInstances,
    instances,
    stopExperiment,
    dismissExperiment,
  ]);

  useHotkeys(
    "mod+enter",
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleRunning();
    },
    {
      enableOnFormTags: true,
      enableOnContentEditable: true,
      preventDefault: true,
    }
  );

  return (
    <button
      type="button"
      css={isRunning ? stopBtnCSS : runBtnCSS}
      onClick={toggleRunning}
    >
      <Icon
        svg={
          isRunning ? <Icons.LoadingOutline /> : <Icons.PlayCircleOutline />
        }
      />
      <span>{isRunning ? "Stop" : "Run"}</span>
      <span css={kbdWrapCSS}>
        <Keyboard>
          <VisuallyHidden>{modifierKey}</VisuallyHidden>
          <span aria-hidden="true">{modifierKey === "Cmd" ? "⌘" : "Ctrl"}</span>
          <VisuallyHidden>enter</VisuallyHidden>
          <span aria-hidden="true">⏎</span>
        </Keyboard>
      </span>
    </button>
  );
}
